/**
 * Trial Service - Manages free tier usage and limits
 * Free tier includes:
 * - Quiz access for first 3 days of roadmap
 * - 3 free messages per day in contextual tutor
 */

interface TrialUsage {
  date: string; // YYYY-MM-DD
  messageCount: number;
}

// Quiz trial: check if within first 3 days of roadmap
export const isQuizTrialAvailable = (roadmapStartDate: string | null | undefined): boolean => {
  if (!roadmapStartDate) return false;

  try {
    const startDate = new Date(roadmapStartDate);
    const today = new Date();
    
    // Calculate days elapsed
    const diffTime = today.getTime() - startDate.getTime();
    const daysPassed = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    // Quiz available for first 3 days (days 0, 1, 2)
    return daysPassed < 3;
  } catch (e) {
    console.error('Error calculating quiz trial availability:', e);
    return false;
  }
};

// Calculate days remaining in quiz trial
export const getQuizTrialDaysRemaining = (roadmapStartDate: string | null | undefined): number => {
  if (!roadmapStartDate) return 0;

  try {
    const startDate = new Date(roadmapStartDate);
    const today = new Date();
    
    const diffTime = today.getTime() - startDate.getTime();
    const daysPassed = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    const daysRemaining = Math.max(0, 3 - daysPassed - 1); // -1 to show current day as day 1
    return daysRemaining;
  } catch (e) {
    console.error('Error calculating quiz trial days:', e);
    return 0;
  }
};

// Get today's date in YYYY-MM-DD format
const getTodayString = (): string => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};

// Get free messages used today
export const getFreeMessagesUsedToday = (userId: string): number => {
  try {
    const storageKey = `nanolez_tutor_trial_${userId}`;
    const stored = localStorage.getItem(storageKey);
    
    if (!stored) return 0;

    const usage: TrialUsage = JSON.parse(stored);
    const today = getTodayString();

    // Reset if it's a new day
    if (usage.date !== today) {
      return 0;
    }

    return usage.messageCount || 0;
  } catch (e) {
    console.error('Error getting free messages used:', e);
    return 0;
  }
};

// Increment free messages counter
export const incrementFreeMessages = (userId: string): number => {
  try {
    const storageKey = `nanolez_tutor_trial_${userId}`;
    const today = getTodayString();
    
    const stored = localStorage.getItem(storageKey);
    let usage: TrialUsage;

    if (!stored) {
      usage = { date: today, messageCount: 0 };
    } else {
      usage = JSON.parse(stored);
      // Reset if it's a new day
      if (usage.date !== today) {
        usage = { date: today, messageCount: 0 };
      }
    }

    usage.messageCount = (usage.messageCount || 0) + 1;
    localStorage.setItem(storageKey, JSON.stringify(usage));

    return usage.messageCount;
  } catch (e) {
    console.error('Error incrementing free messages:', e);
    return 0;
  }
};

// Check if messages are available today
export const canSendFreeTutorMessage = (userId: string): boolean => {
  const messagesUsed = getFreeMessagesUsedToday(userId);
  return messagesUsed < 3;
};

// Get remaining free messages for today
export const getRemainingFreeTutorMessages = (userId: string): number => {
  const messagesUsed = getFreeMessagesUsedToday(userId);
  return Math.max(0, 3 - messagesUsed);
};

// Reset trial (for testing/admin purposes)
export const resetTutorTrialForDay = (userId: string): void => {
  try {
    const storageKey = `nanolez_tutor_trial_${userId}`;
    localStorage.removeItem(storageKey);
  } catch (e) {
    console.error('Error resetting trial:', e);
  }
};
